import type { VoucherProviderResponse } from '@/api/model/voucherProviderResponse';
import { ProviderCategory } from '@/api/model/providerCategory';
import { useDeleteProvider, useListProviders } from '@/api/voucher-providers/voucher-providers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { Search, ArrowUpDown, Pencil, Trash2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';
import { VoucherPagination } from '@/components/vouchers/VoucherPagination';

const ITEMS_PER_PAGE = 10;

export const VoucherProviderManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: providersData, isLoading, isError, error } = useListProviders();

  const deleteProviderMutation = useDeleteProvider();

  const providers: VoucherProviderResponse[] = React.useMemo(
    () => (providersData?.items || []) as VoucherProviderResponse[],
    [providersData?.items]
  );

  // Local UI state for admin toolbar
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('alle');
  const [selectedStatus, setSelectedStatus] = useState<string>('alle');
  const [selectedSort, setSelectedSort] = useState<string>('neueste');
  const [currentPage, setCurrentPage] = useState<number>(1);

  const handleDeleteProvider = useCallback(
    (providerId: string) => {
      deleteProviderMutation.mutate(
        { providerId },
        {
          onSuccess: () => {
            toast.success('Anbieter wurde gelöscht');
            queryClient.invalidateQueries({ queryKey: ['listProviders'] });
          },
          onError: error => {
            toast.error(getApiErrorMessage(error, 'Fehler beim Löschen des Partners'));
          },
        }
      );
    },
    [deleteProviderMutation, queryClient]
  );

  // Apply filters and sorting - must be before early returns to maintain hook order
  const filteredProviders = React.useMemo(() => {
    let filtered = [...providers];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name.toLowerCase().includes(query) ||
          p.email.toLowerCase().includes(query) ||
          p.address.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'alle') {
      filtered = filtered.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());
    }

    // Status filter
    if (selectedStatus !== 'alle') {
      const isActive = selectedStatus === 'aktiv';
      filtered = filtered.filter(p => p.is_active === isActive);
    }

    // Sorting
    switch (selectedSort) {
      case 'neueste':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'aelteste':
        filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'name-az':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-za':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    return filtered;
  }, [providers, searchQuery, selectedCategory, selectedStatus, selectedSort]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, selectedStatus, selectedSort]);

  const totalPages = React.useMemo(() => {
    if (filteredProviders.length === 0) {
      return 1;
    }
    return Math.ceil(filteredProviders.length / ITEMS_PER_PAGE);
  }, [filteredProviders.length]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedProviders = React.useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProviders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredProviders, currentPage]);

  if (isLoading) {
    return (
      <div className='bg-white rounded-xl border p-6'>
        <p className='text-center text-muted-foreground'>Partner werden geladen...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className='bg-white rounded-xl border p-6'>
        <p className='text-center text-red-500'>
          Fehler beim Laden der Partner: {error instanceof Error ? error.message : 'Unbekannter Fehler'}
        </p>
      </div>
    );
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'bg-swd-gruen-light text-swd-tuerkis-dark border-0'
      : 'bg-neutral-hellgrau text-neutral-dunkelgrau border-0';
  };

  const getCategoryDisplayName = (category: ProviderCategory): string => {
    switch (category) {
      case ProviderCategory.GASTRONOMIE:
        return 'Gastronomie';
      case ProviderCategory.ERLEBEN:
        return 'Erleben';
      case ProviderCategory.SHOPPING:
        return 'Shopping';
      case ProviderCategory.SERVICES:
        return 'Services';
      default:
        return category;
    }
  };

  return (
    <div className='space-y-6'>
      {/* Page header (not a card) */}
      <section className='flex items-end justify-between gap-4'>
        <div className='flex flex-col gap-2'>
          <CardTitle className='text-swd-tuerkis-darker text-3xl leading-9 font-semibold'>Partner</CardTitle>
          <CardDescription className='text-neutral-grau tracking-wide'>
            Verwalten, erstellen und bearbeiten Sie alle Gutscheinpartner im System.
          </CardDescription>
        </div>
        <div className='flex items-end gap-3'>
          <Button asChild size='sm' variant='enabled'>
            <Link to='/admin/providers/create'>Neuen Partner erstellen</Link>
          </Button>
        </div>
      </section>

      {/* Toolbar below header - filters */}
      <section className='flex items-center justify-between gap-3 flex-wrap rounded-xl border border-neutral-hellgrau bg-white px-3 py-4'>
        <div className='flex items-center gap-3 flex-wrap'>
          {/* Search */}
          <div className='relative'>
            <Input
              placeholder='Suche nach Name, E-Mail oder Adresse...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className='pr-8'
            />
            <Search className='absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none' />
          </div>

          {/* Kategorie */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Alle Kategorien' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Alle Kategorien</SelectItem>
              <SelectItem value='gastronomie'>Gastronomie</SelectItem>
              <SelectItem value='erleben'>Erleben</SelectItem>
              <SelectItem value='shopping'>Shopping</SelectItem>
              <SelectItem value='services'>Services</SelectItem>
            </SelectContent>
          </Select>

          {/* Status */}
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger size='sm'>
              <SelectValue placeholder='Status Alle' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='alle'>Status Alle</SelectItem>
              <SelectItem value='aktiv'>Aktiv</SelectItem>
              <SelectItem value='inaktiv'>Inaktiv</SelectItem>
            </SelectContent>
          </Select>

          {/* Sortieren */}
          <Select value={selectedSort} onValueChange={setSelectedSort}>
            <SelectTrigger size='sm' className='whitespace-nowrap'>
              <ArrowUpDown className='mr-2 h-4 w-4' />
              <SelectValue placeholder='Sortieren nach Neueste' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='neueste'>Neueste</SelectItem>
              <SelectItem value='aelteste'>Älteste</SelectItem>
              <SelectItem value='name-az'>Name A–Z</SelectItem>
              <SelectItem value='name-za'>Name Z–A</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Content area */}
      {filteredProviders.length === 0 ? (
        <div className='text-center py-8 bg-white rounded-xl border border-neutral-hellgrau'>
          <p className='text-muted-foreground mb-4'>Keine Partner gefunden</p>
        </div>
      ) : (
        <div className='rounded-xl border border-neutral-hellgrau overflow-hidden bg-white'>
          <Table className='min-w-[960px]'>
            <TableHeader className='bg-swd-hellblau'>
              <TableRow>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Name
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  E-Mail
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Adresse
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Telefon
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Kategorie
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Status
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap'>
                  Erstellt
                </TableHead>
                <TableHead className='text-swd-tuerkis-darker font-semibold tracking-wide whitespace-nowrap w-[100px] text-center'>
                  Aktionen
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedProviders.map((provider: VoucherProviderResponse) => (
                <TableRow key={provider.id} className='hover:bg-swd-hellblau/40 border-b border-neutral-hellgrau'>
                  <TableCell className='font-medium text-neutral-dunkelgrau align-middle'>{provider.name}</TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>{provider.email}</TableCell>
                  <TableCell
                    className='align-middle font-medium text-neutral-grau max-w-xs truncate'
                    title={provider.address}
                  >
                    {provider.address}
                  </TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>{provider.phone || '—'}</TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>
                    {getCategoryDisplayName(provider.category)}
                  </TableCell>
                  <TableCell className='align-middle'>
                    <div className='flex items-center gap-2 flex-wrap'>
                      <Badge className={`rounded-2xl px-2 py-0.5 font-bold ${getStatusColor(provider.is_active)}`}>
                        {provider.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className='align-middle font-medium text-neutral-grau'>
                    {new Date(provider.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className='text-center align-middle'>
                    <div className='flex items-center justify-center gap-2'>
                      <Button variant='ghost' size='sm' asChild className='h-8 w-8 p-0'>
                        <Link to={`/admin/providers/${provider.id}`}>
                          <Pencil className='h-4 w-4 text-swd-tuerkis-darker' />
                        </Link>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                            <Trash2 className='h-4 w-4 text-swd-tuerkis-darker' />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Partner löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Möchten Sie den Partner "{provider.name}" wirklich löschen? Diese Aktion kann nicht
                              rückgängig gemacht werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteProvider(provider.id)}
                              disabled={deleteProviderMutation.isPending}
                            >
                              {deleteProviderMutation.isPending ? 'Wird gelöscht...' : 'Löschen'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className='border-t border-neutral-hellgrau bg-white px-6 py-4'>
              <VoucherPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalVouchers={filteredProviders.length}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
